import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, MenuItem, TextField, useTheme } from "@mui/material";
import Button from "components/basic/button";
import Modal from "components/basic/modal";
import Typography from "components/basic/typography";
import { TicketCategory } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import { MAX_MESSAGE_LENGTH } from "pages/dashboard/pages/message/constants";

import AttachmentPicker from "./components/AttachmentPicker";
import { CATEGORY_LABELS, MAX_SUBJECT_LENGTH } from "../../constants";
import { useTicketActions } from "../../supportApi";

interface NewTicketDialogProps {
  // ev-portal-app passes its slug so replies link back to that portal.
  portalSlug?: string;
  onClose: () => void;
  onCreated: (ticketId: number) => void;
}

const NewTicketDialog: React.FC<NewTicketDialogProps> = ({
  portalSlug,
  onClose,
  onCreated,
}) => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("support");
  const { create } = useTicketActions(false);

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState<TicketCategory | "">("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    subject.trim().length > 0 &&
    category !== "" &&
    (body.trim().length > 0 || files.length > 0) &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    const result = await create({
      subject: subject.trim(),
      category: category as TicketCategory,
      body: body.trim(),
      files,
      portalSlug,
    });
    setSubmitting(false);

    if (!result.ok || !result.ticketId) {
      showSnack(result.error ?? t("snack.open_failed"), "error");
      return;
    }
    showSnack(t("snack.opened"), "success");
    onCreated(result.ticketId);
  };

  // iOS Safari zooms on focused inputs under 16px.
  const inputSx = { "& .MuiInputBase-input": { fontSize: { xs: 16, sm: 14 } } };

  return (
    <Modal onClose={onClose}>
      <Box
        component="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          handleSubmit();
        }}
        sx={{
          width: { xs: "min(340px, 100vw - 64px)", sm: 480 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" style={{ fontWeight: "bold" }}>
            {t("dialog.title")}
          </Typography>
          <Typography
            variant="body2"
            style={{ color: theme.palette.text.secondary, marginTop: 4 }}
          >
            {t("dialog.intro")}
          </Typography>
        </Box>

        <TextField
          label={t("dialog.subject")}
          size="small"
          required
          sx={inputSx}
          value={subject}
          onChange={(e) =>
            setSubject(e.target.value.slice(0, MAX_SUBJECT_LENGTH))
          }
        />

        <TextField
          select
          label={t("dialog.category")}
          size="small"
          required
          sx={inputSx}
          value={category}
          onChange={(e) => setCategory(e.target.value as TicketCategory)}
        >
          {Object.values(TicketCategory).map((value) => (
            <MenuItem key={value} value={value}>
              {t(CATEGORY_LABELS[value])}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label={t("dialog.description")}
          multiline
          minRows={4}
          maxRows={10}
          size="small"
          sx={inputSx}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
        />

        <AttachmentPicker
          files={files}
          disabled={submitting}
          onChange={setFiles}
        />

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="text" onClick={onClose} disabled={submitting}>
            {t("dialog.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={!canSubmit}
            loading={submitting}
            onClick={handleSubmit}
          >
            {t("dialog.submit")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default NewTicketDialog;
